import { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { PatternEvidence } from "@/components/app/PatternAnswerReview";

const RESPONSES = [
  { code: "yes_definitely", label: "Yes, definitely" },
  { code: "probably", label: "Probably" },
  { code: "not_sure", label: "Not sure" },
  { code: "no", label: "No" },
];

export function patternAck(code, action, reflected = false) {
  const read = reflected ? " Our Current Read now reflects this." : "";
  return ackBase(code, action) + read;
}

function ackBase(code, action) {
  if (action === "confirmed") {
    return code === "probably"
      ? "Saved as a leaning, not a confirmation. You'll find it in My Realisations, where you can change it."
      : "Saved — this is now in My Realisations, where you can change it any time.";
  }
  if (action === "deferred") return "Noted — you're not sure yet. Nothing has been saved as a Realisation, and we won't ask this again.";
  return "Got it — we won't treat that as a Realisation.";
}

export default function RealisationsPatternCard({ prompt, onDone }) {
  const [saving, setSaving] = useState(null);
  const [failed, setFailed] = useState(null);

  if (!prompt) return null;

  const respond = async (code) => {
    if (saving) return;
    setSaving(code);
    setFailed(null);
    try {
      const { data } = await api.post(
        "/realisations/patterns/" + prompt.candidate_id + "/respond",
        { response: code }
      );
      onDone?.(patternAck(code, data?.action, data?.reflected_in_current_read));
    } catch {
      setFailed(code);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card
      className="mt-6 border-[#4C1D95]/20 bg-[#FDFBF7] p-5 sm:p-6"
      data-testid="realisations-pattern-card"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#4C1D95]/10 p-2 text-[#4C1D95]">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-uppercase text-[#4C1D95]">A pattern is emerging</div>
          <h2 className="font-editorial text-xl sm:text-2xl mt-1 text-stone-900" data-testid="realisations-pattern-question">
            {prompt.question}
          </h2>
          <PatternEvidence courses={prompt.evidence_courses} testId="realisations-pattern-evidence" />
          <p className="text-sm text-stone-600 mt-2" data-testid="realisations-pattern-helper">{prompt.helper_text}</p>

          <div className="mt-4 rounded-md border border-stone-200 bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-stone-500">
              Possible Realisation
            </div>
            <div className="text-sm sm:text-base text-stone-900 mt-1">
              {prompt.statement}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {RESPONSES.map((option) => (
              <button
                key={option.code}
                type="button"
                data-testid={"realisations-pattern-" + option.code}
                disabled={Boolean(saving)}
                onClick={() => respond(option.code)}
                className={
                  "min-h-9 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] focus-visible:ring-offset-2 " +
                  (option.code === "yes_definitely"
                    ? "border-[#4C1D95] bg-[#4C1D95] text-white hover:bg-[#3d1778]"
                    : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]") +
                  (saving ? " opacity-60" : "")
                }
              >
                {saving === option.code ? "Saving…" : option.label}
              </button>
            ))}
          </div>

          {failed && (
            <div role="alert" className="mt-3 flex flex-wrap items-center gap-3 text-sm text-rose-700" data-testid="realisations-pattern-error">
              We couldn't save that just now — nothing has been changed.
              <button type="button" onClick={() => respond(failed)} data-testid="realisations-pattern-retry"
                className="font-semibold underline underline-offset-2">Try again</button>
            </div>
          )}

          <p className="mt-3 text-xs text-stone-500">
            We only save this as a Realisation if you agree.
          </p>
        </div>
      </div>
    </Card>
  );
}
